import { resolveMediaUrl } from "@/components/common/MediaFrame";

/**
 * Extrae la URL de miniatura de un activo de tipo vídeo si está disponible.
 */
export const extractThumbnailUrl = (asset) => {
  if (!asset || typeof asset !== "object") return null;
  const thumb =
    asset.thumbnailUrl ||
    asset.posterUrl ||
    asset.thumbUrl ||
    asset.thumbnail_url ||
    asset.poster_url ||
    asset.thumbnail;

  if (typeof thumb === "string" && thumb.trim()) {
    return thumb.trim();
  }
  return null;
};

/**
 * Determina si un recurso pertenece legítimamente al proyecto y está autorizado
 * para usarse en su portada de presentación.
 * 
 * Reglas de seguridad e invariantes:
 * - El activo debe pertenecer estrictamente al proyecto (asset.projectId === project.id).
 * - En proyectos normales (!project.isDemo), se prohíbe el uso de activos demo (asset.isDemo o asset.source === "demo").
 * - Debe tener una URL válida o una miniatura disponible.
 */
export const isAuthorizedProjectAsset = (asset, project, state = {}) => {
  if (!asset || typeof asset !== "object" || !project || !project.id) return false;

  // Rechazar activos pertenecientes explícitamente a otro proyecto (ámbito ajeno no global)
  if (asset.projectId && asset.projectId !== project.id && asset.scope !== "global") {
    return false;
  }

  // No usar recursos demo en proyectos que no sean demo
  if (!project.isDemo && (asset.isDemo === true || asset.source === "demo")) {
    return false;
  }

  // Comprobar pertenencia legítima:
  // 1. Asignación directa al proyecto o ámbito global
  const isDirect = asset.projectId === project.id || asset.scope === "global";
  // 2. Portada explícita elegida para el proyecto
  const isExplicitCover = project.coverAssetId === asset.id || project.cover === asset.id;
  // 3. Activo vinculado a través de un personaje perteneciente a este proyecto
  const isProjectCharacterAsset = Boolean(
    (state?.characters || []).some(
      (c) =>
        c.projectId === project.id &&
        (c.masterAssetId === asset.id ||
          (c.viewAssetIds || []).includes(asset.id) ||
          (c.views || []).some((v) => v.assetId === asset.id))
    )
  );
  // 4. Activo sin proyecto asignado (p. ej. biblioteca general o generado en QuickTool)
  const isUnassigned = !asset.projectId;

  if (!isDirect && !isExplicitCover && !isProjectCharacterAsset && !isUnassigned) {
    return false;
  }

  const thumb = extractThumbnailUrl(asset);
  const hasUrl = typeof asset.url === "string" && Boolean(asset.url.trim());
  const hasThumb = Boolean(thumb);

  return hasUrl || hasThumb;
};

/**
 * Transforma un recurso en un candidato de presentación para la tarjeta de portada.
 * Si es un vídeo y dispone de miniatura, se transforma en un candidato de imagen
 * apuntando a dicha miniatura.
 */
export const toPresentationCandidate = (asset) => {
  if (!asset) return null;
  const thumb = extractThumbnailUrl(asset);

  if (asset.type === "video" && thumb) {
    return {
      ...asset,
      type: "image",
      url: thumb,
      isThumbnail: true,
      originalType: "video",
    };
  }

  return asset;
};

/**
 * Obtiene la lista ordenada de candidatos a portada para un proyecto según las
 * prioridades de presentación:
 * 1. Portada explícita del proyecto (project.coverAssetId o project.cover).
 * 2. Medio seleccionado en una escena del proyecto (ordenadas por scene.order).
 * 3. Imágenes del proyecto (ordenadas de más recientes a más antiguas).
 * 4. Vídeos del proyecto con miniatura disponible (y vídeos como último recurso).
 * 
 * Garantiza que:
 * - Solo se seleccionan recursos autorizados del propio proyecto.
 * - No hay candidatos duplicados (por id o por url).
 * - No altera la base de datos ni modifica el estado de escenas/proyectos.
 */
export const getProjectCoverCandidates = (project, state = {}, assetById = {}) => {
  if (!project || !project.id) return [];

  const projectId = project.id;
  const candidates = [];
  const seenUrls = new Set();
  const seenIds = new Set();

  const lookupAsset = (assetId) => {
    if (!assetId) return null;
    if (assetById && assetById[assetId]) return assetById[assetId];
    return (state.assets || []).find((a) => a.id === assetId) || null;
  };

  const addCandidate = (rawAsset) => {
    if (!isAuthorizedProjectAsset(rawAsset, project, state)) return;
    const candidate = toPresentationCandidate(rawAsset);
    if (!candidate || !candidate.url || typeof candidate.url !== "string" || !candidate.url.trim()) {
      return;
    }

    const trimmedUrl = candidate.url.trim();
    if (seenUrls.has(trimmedUrl) || (candidate.id && seenIds.has(candidate.id))) {
      return;
    }

    seenUrls.add(trimmedUrl);
    if (candidate.id) seenIds.add(candidate.id);
    candidates.push({ ...candidate, url: trimmedUrl });
  };

  // 1. Portada explícita asignada al proyecto
  if (project.coverAssetId) {
    const asset = lookupAsset(project.coverAssetId);
    if (asset) addCandidate(asset);
  }
  if (project.cover) {
    if (typeof project.cover === "string") {
      const asset = lookupAsset(project.cover);
      if (asset) addCandidate(asset);
    } else if (typeof project.cover === "object") {
      addCandidate(project.cover);
    }
  }

  // 2. Medio seleccionado de una escena del proyecto
  const projectScenes = (state.scenes || [])
    .filter((s) => s.projectId === projectId && s.selectedAssetId)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  for (const scene of projectScenes) {
    const asset = lookupAsset(scene.selectedAssetId);
    if (asset) addCandidate(asset);
  }

  // 3. Imágenes del proyecto (más recientes primero)
  const projectAssets = (state.assets || []).filter((a) => a.projectId === projectId);

  const imageAssets = projectAssets
    .filter((a) => a.type === "image" || !a.type)
    .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));

  for (const img of imageAssets) {
    addCandidate(img);
  }

  // 4. Vídeos del proyecto: primero aquellos con miniatura disponible
  const videoAssets = projectAssets.filter((a) => a.type === "video");

  const videosWithThumb = videoAssets
    .filter((v) => Boolean(extractThumbnailUrl(v)))
    .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));

  for (const vid of videosWithThumb) {
    addCandidate(vid);
  }

  const videosWithoutThumb = videoAssets
    .filter((v) => !extractThumbnailUrl(v))
    .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));

  for (const vid of videosWithoutThumb) {
    addCandidate(vid);
  }

  return candidates;
};

/**
 * Devuelve el candidato principal para portada o null si no existe ninguno.
 */
export const getProjectCover = (project, state = {}, assetById = {}) => {
  const candidates = getProjectCoverCandidates(project, state, assetById);
  return candidates.length > 0 ? candidates[0] : null;
};
